import { Plug } from "lucide-react";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";

import {
  DataTable,
  NARROW_ONLY,
  SortableHeadCell,
  SortButton,
  TABLE_ICON_CLASS,
  TableCell,
  TableEmptyRow,
  TableFacts,
  TableHead,
  TableHeadCell,
  TableItem,
} from "@/components/data-table";
import { FilterBar } from "@/components/filter-bar";
import { Highlight } from "@/components/highlight";
import { IssueStatusChip } from "@/components/issue-status-chip";
import { MultiSelect } from "@/components/multi-select";
import { PeriodSelect } from "@/components/period-select";
import { SearchMultiSelect } from "@/components/search-multi-select";
import { ToolIcon } from "@/components/tool-icon";
import { RetiredChip } from "@/components/tool-name";
import type { McpIssue } from "@/lib/api";
import { formatDateTime, formatNumber } from "@/lib/format";
import {
  ISSUE_CATEGORIES,
  ISSUE_STATUSES,
  issueCategoryLabel,
  issueKindLabel,
  issuePath,
  issueStatusLabel,
  issueToolLabel,
  statusRank,
} from "@/lib/issues";
import type { Period } from "@/lib/period";
import { onControl } from "@/lib/row-click";
import { matchesSearch } from "@/lib/search";
import { factLine } from "@/lib/table-facts";
import { sortRows, sortTime, useUrlTableSort } from "@/lib/table-sort";
import { isRetiredTool, toolLabel, useToolTitles } from "@/lib/tool-titles";
import { cn } from "@/lib/utils";

import { failuresListState } from "../utils";
import { Sparkline } from "./sparkline";

/** The list's filters in the page's URL: `?q=odoo&status=new&status=regressed&category=…&tool=…`. */
export const ISSUE_PARAMS = { search: "q", status: "status", category: "category", tool: "tool" };

/** Problem, status, times, people, last seen, first seen, per day. */
const COLUMNS = 7;

const SORT_COLUMNS = {
  problem: "asc",
  status: "asc",
  count: "desc",
  users: "desc",
  last: "desc",
  first: "desc",
} as const;

type SortKey = keyof typeof SORT_COLUMNS;

/** The values of a repeated search parameter that are among `known`. */
function knownValues<T extends string>(values: string[], known: readonly T[]): T[] {
  return values.filter((value): value is T => (known as readonly string[]).includes(value));
}

/** The issue list's filters, kept in the page's URL (`ISSUE_PARAMS`), for its filter bar and its table. */
export function useIssueFilters() {
  const [params, setParams] = useSearchParams();
  const search = params.get(ISSUE_PARAMS.search) ?? "";
  const statuses = knownValues(params.getAll(ISSUE_PARAMS.status), ISSUE_STATUSES);
  const categories = knownValues(params.getAll(ISSUE_PARAMS.category), ISSUE_CATEGORIES);
  const tools = params.getAll(ISSUE_PARAMS.tool);
  const setParam = (name: string, values: readonly string[]) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(name);
        values.filter((value) => value !== "").forEach((value) => next.append(name, value));
        return next;
      },
      { replace: true },
    );
  const filtered =
    search !== "" || statuses.length > 0 || categories.length > 0 || tools.length > 0;
  const reset = () =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        Object.values(ISSUE_PARAMS).forEach((name) => next.delete(name));
        return next;
      },
      { replace: true },
    );
  return { search, statuses, categories, tools, setParam, filtered, reset };
}

interface IssueFilterBarProps {
  /** The period's issues, whose tools the tool filter offers. */
  issues: readonly McpIssue[];
  days: Period;
  onDaysChange: (days: Period) => void;
}

/**
 * Fouten's filter bar, in the tinted box the page opens with: the period,
 * which the chart and the list follow, then the list's filters (a search, the
 * status, the kind of failure and the tool), kept in the URL. "Filters
 * wissen" clears the list's filters; the period is a view and stays.
 */
export function IssueFilterBar({ issues, days, onDaysChange }: IssueFilterBarProps) {
  const { t } = useTranslation();
  const titles = useToolTitles();
  const { search, statuses, categories, tools, setParam, filtered, reset } = useIssueFilters();
  const toolNames = [...new Set(issues.flatMap((issue) => (issue.tool ? [issue.tool] : [])))];
  return (
    <FilterBar
      tinted
      search={{
        value: search,
        onChange: (value) => setParam(ISSUE_PARAMS.search, [value]),
        label: t("issues.list.search"),
      }}
      // The period takes a row of its own on a phone; the status, the kind and the tool
      // fit "narrow", so the bar keeps one line at 1440 px.
      columns={["mediumRow", "narrow", "narrow", "narrow"]}
      filtered={filtered}
      onReset={reset}
    >
      <PeriodSelect value={days} onChange={onDaysChange} />
      <MultiSelect
        size="sm"
        className="w-full"
        options={ISSUE_STATUSES.map((status) => ({
          value: status,
          label: issueStatusLabel(status),
        }))}
        value={statuses}
        onChange={(value) => setParam(ISSUE_PARAMS.status, value)}
        label={choiceLabel(statuses, issueStatusLabel, t("issues.list.allStatuses"))}
        name={t("issues.columns.status")}
        allLabel={t("issues.list.allStatuses")}
      />
      <MultiSelect
        size="sm"
        className="w-full"
        options={ISSUE_CATEGORIES.map((category) => ({
          value: category,
          label: issueCategoryLabel(category),
        }))}
        value={categories}
        onChange={(value) => setParam(ISSUE_PARAMS.category, value)}
        label={choiceLabel(categories, issueCategoryLabel, t("issues.list.allCategories"))}
        name={t("issues.list.category")}
        allLabel={t("issues.list.allCategories")}
      />
      <SearchMultiSelect
        className="w-full"
        options={toolNames.map((name) => ({
          value: name,
          label: toolLabel(titles, name),
          detail: name,
          icon: <ToolIcon name={name} className="text-primary size-4" />,
          note: isRetiredTool(titles, name) ? t("tools.retired.label") : undefined,
        }))}
        value={tools}
        onChange={(value) => setParam(ISSUE_PARAMS.tool, value)}
        monoDetail
        name={t("analytics.tool")}
        allLabel={t("analytics.allTools")}
        countLabel={(count) => t("filters.toolCount", { count })}
        searchLabel={t("analytics.searchTool")}
      />
    </FilterBar>
  );
}

interface IssueTableProps {
  issues: readonly McpIssue[];
  days: Period;
}

/**
 * The period's issues: every failed call belongs to one, the same kind of
 * failure in the same tool (or in the connector as a whole). Each row names
 * the problem (the kind, the tool, and the key muted), its status as a chip,
 * how often it failed in the period and for how many people, when it was
 * last and first seen, and its failures per day as a small bar row. Every
 * column sorts; by default what still needs a look comes first (new and
 * come back together), then the rest by status, each status the latest
 * failure first. The page's filter bar (`IssueFilterBar`) filters it: a
 * search, the status, the kind of failure and the tool, all kept in the URL,
 * so the home page can link to the issues Velta is on. A row opens its
 * issue's page, whose way back returns to the list as it was left.
 */
export function IssueTable({ issues, days }: IssueTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // An issue's page goes back to the list as it is now, its filters and its sort.
  const linkState = failuresListState(location.search);
  const titles = useToolTitles();
  const { search, statuses, categories, tools, filtered, reset } = useIssueFilters();
  const sort = useUrlTableSort<SortKey>({
    columns: SORT_COLUMNS,
    initial: { key: "status", dir: "asc" },
  });

  // Every sort keeps the latest failure first among equals (rows with the same value keep
  // their order), so the first sort, on the status, reads new and come back by their last failure.
  const latestFirst = sortRows(issues, { key: "last", dir: "desc" }, (issue) =>
    sortTime(issue.last_seen),
  );
  const shown = sortRows(
    latestFirst.filter(
      (issue) =>
        matchesSearch(
          [
            issueKindLabel(issue.error_type),
            issueToolLabel(issue.tool, titles),
            issue.key,
            issueCategoryLabel(issue.category),
          ],
          search,
        ) &&
        (statuses.length === 0 || statuses.includes(issue.status)) &&
        (categories.length === 0 || categories.includes(issue.category)) &&
        (tools.length === 0 || (issue.tool !== null && tools.includes(issue.tool))),
    ),
    sort.sort,
    (issue, key) => {
      switch (key) {
        case "problem":
          return issueKindLabel(issue.error_type);
        case "status":
          return statusRank(issue.status);
        case "count":
          return issue.count;
        case "users":
          return issue.users;
        case "last":
          return sortTime(issue.last_seen);
        case "first":
          return sortTime(issue.first_seen);
      }
    },
  );
  // A click on the row opens the issue, but not one on a link or a chip in it.
  const onRowClick = (event: MouseEvent<HTMLTableRowElement>, issue: McpIssue) => {
    if (!onControl(event)) {
      void navigate(issuePath(issue.key, days), { state: linkState });
    }
  };

  return (
    <DataTable label={t("issues.list.label")}>
      <TableHead>
        <SortableHeadCell
          {...sort.head("problem")}
          after={
            // The status's sort, the list's first, stays in the phone layout.
            <span className={cn("ml-3", NARROW_ONLY)}>
              <SortButton {...sort.head("status")} sayOrder>
                {t("issues.columns.status")}
              </SortButton>
            </span>
          }
        >
          {t("issues.columns.problem")}
        </SortableHeadCell>
        <SortableHeadCell {...sort.head("status")} narrow="hide">
          {t("issues.columns.status")}
        </SortableHeadCell>
        <SortableHeadCell {...sort.head("count")} align="right">
          {t("issues.columns.count")}
        </SortableHeadCell>
        <SortableHeadCell
          {...sort.head("users")}
          align="right"
          narrow="hide"
          className="@max-3xl:hidden"
        >
          {t("issues.columns.users")}
        </SortableHeadCell>
        <SortableHeadCell {...sort.head("last")} narrow="hide">
          {t("issues.columns.lastSeen")}
        </SortableHeadCell>
        {/* Leave when the table's own room is tight (a narrow window beside the full sidebar). */}
        <SortableHeadCell {...sort.head("first")} narrow="hide" className="@max-4xl:hidden">
          {t("issues.columns.firstSeen")}
        </SortableHeadCell>
        {/* The failures per day draw the times column's count: its sort is that column's. */}
        <TableHeadCell narrow="hide" className="whitespace-nowrap @max-3xl:hidden">
          {t("issues.columns.perDay")}
        </TableHeadCell>
      </TableHead>
      <tbody>
        {shown.map((issue) => (
          <tr
            key={issue.key}
            className="hover:bg-accent-light/40 cursor-pointer border-t"
            onClick={(event) => onRowClick(event, issue)}
          >
            <TableCell>
              <TableItem
                icon={
                  issue.tool === null ? (
                    <Plug aria-hidden="true" strokeWidth={1.75} className={TABLE_ICON_CLASS} />
                  ) : (
                    <ToolIcon name={issue.tool} className={TABLE_ICON_CLASS} />
                  )
                }
                title={
                  <Link
                    to={issuePath(issue.key, days)}
                    state={linkState}
                    className="hover:underline"
                  >
                    <Highlight text={issueKindLabel(issue.error_type)} query={search} />
                  </Link>
                }
                summary={
                  <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span>
                      <Highlight text={issueToolLabel(issue.tool, titles)} query={search} />
                    </span>
                    <IssueKey issueKey={issue.key} search={search} />
                    {issue.tool !== null && isRetiredTool(titles, issue.tool) && <RetiredChip />}
                  </span>
                }
                facts={
                  <>
                    <IssueStatusChip status={issue.status} />
                    <TableFacts>
                      {factLine([
                        t("issues.list.lastSeenFact", { time: formatDateTime(issue.last_seen) }),
                        t("issues.list.usersFact", { count: issue.users }),
                      ])}
                    </TableFacts>
                  </>
                }
              />
            </TableCell>
            <TableCell narrow="hide">
              <IssueStatusChip status={issue.status} />
            </TableCell>
            <TableCell align="right" className="font-medium tabular-nums">
              {formatNumber(issue.count)}
            </TableCell>
            <TableCell align="right" narrow="hide" className="tabular-nums @max-3xl:hidden">
              {formatNumber(issue.users)}
            </TableCell>
            <TableCell narrow="hide" className="text-muted whitespace-nowrap tabular-nums">
              {formatDateTime(issue.last_seen)}
            </TableCell>
            <TableCell
              narrow="hide"
              className="text-muted whitespace-nowrap tabular-nums @max-4xl:hidden"
            >
              {formatDateTime(issue.first_seen)}
            </TableCell>
            <TableCell narrow="hide" className="@max-3xl:hidden">
              <Sparkline days={issue.per_day} />
            </TableCell>
          </tr>
        ))}
        {shown.length === 0 && (
          <TableEmptyRow colSpan={COLUMNS} onReset={filtered ? reset : undefined}>
            {t("issues.list.noMatch")}
          </TableEmptyRow>
        )}
      </tbody>
    </DataTable>
  );
}

/**
 * An issue's key in mono, `<tool>:<kind>`: a long one goes to the next line
 * after the colon, and breaks anywhere only when a part is longer than its
 * line, never hyphenated, so it never widens the table.
 */
function IssueKey({ issueKey, search }: { issueKey: string; search: string }) {
  const colon = issueKey.indexOf(":");
  return (
    <code className="font-mono wrap-anywhere hyphens-none">
      {colon === -1 ? (
        <Highlight text={issueKey} query={search} />
      ) : (
        <>
          <Highlight text={issueKey.slice(0, colon + 1)} query={search} />
          <wbr />
          <Highlight text={issueKey.slice(colon + 1)} query={search} />
        </>
      )}
    </code>
  );
}

/** What a multiple choice's button says: "Alle statussen", or the chosen ones' names. */
function choiceLabel<T extends string>(
  chosen: readonly T[],
  label: (value: T) => string,
  all: string,
): string {
  return chosen.length === 0 ? all : chosen.map(label).join(", ");
}
